import type { InputHTMLAttributes } from 'react'

import { Icon } from './Icon'

/**
 * The search field, in the two shapes the design actually draws.
 *
 * `wallet` (screens 18, 34) is 48 high at radius 14 on `white/3%` with the
 * icon on the left. `market` (screen 39) is 52 high at radius 16 on
 * `white/5%` with the icon on the **right**. They are different enough to
 * need a variant and close enough not to need two components.
 */
interface SearchBarProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  variant?: 'wallet' | 'market'
}

export function SearchBar({
  label,
  variant = 'wallet',
  className = '',
  ...rest
}: SearchBarProps) {
  const isMarket = variant === 'market'
  const placeholder = label ?? (isMarket ? 'جستجوی رمز ارز...' : 'جستجو')

  const input = (
    <input
      type="search"
      aria-label={placeholder}
      placeholder={placeholder}
      className={`min-w-0 flex-1 text-ink placeholder:text-ink-80 ${
        isMarket ? 'text-15' : 'text-14'
      }`}
      {...rest}
    />
  )
  const icon = <Icon name="search" size={isMarket ? 20 : 18} className="shrink-0 text-ink-50" />

  return (
    <div
      className={[
        'flex items-center gap-2 border',
        isMarket
          ? 'h-[52px] rounded-16 border-line-8 bg-surface-5 px-4'
          : 'h-12 rounded-14 border-line-8 bg-surface-3 ps-4 pe-3',
        className,
      ].join(' ')}
    >
      {/* Market puts the icon on the right, so it comes first in RTL. */}
      {isMarket ? (
        <>
          {icon}
          {input}
        </>
      ) : (
        <>
          {input}
          {icon}
        </>
      )}
    </div>
  )
}
